/**
 * The whole router. Four routes, no dependency.
 *
 * Fastify serves index.html for /, /d/:id, /login, /settings/mcp and the old
 * /settings/tokens, so a hard refresh on any of them works; this only handles
 * in-page navigation and the back button.
 */

export type Route =
  | { name: 'list' }
  | { name: 'doc'; id: string }
  | { name: 'login' }
  | { name: 'mcp' }
  | { name: 'unknown'; path: string }

/**
 * Paths that have moved.
 *
 * /settings/tokens became /settings/mcp when the page grew from "mint a
 * credential" into "here is the MCP server, and by the way here are your
 * tokens". The old path is a link people have bookmarked and pasted to each
 * other, so it keeps working -- it is rewritten to the new one rather than
 * answered at, which means one canonical URL in the address bar and no second
 * copy of the page to keep in step.
 */
const MOVED: Record<string, string> = {
  '/settings/tokens': '/settings/mcp',
  '/settings/tokens/': '/settings/mcp',
}

/**
 * The new path for an old one, rewriting the address bar on the way.
 *
 * replaceState rather than pushState: the moved URL should not sit in history
 * where Back would land on it and be redirected forward again.
 */
function canonical(path: string): string {
  const moved = MOVED[path]
  if (!moved) return path
  history.replaceState({}, '', moved + location.search + location.hash)
  return moved
}

function parse(rawPath: string): Route {
  const path = canonical(rawPath)
  const doc = path.match(/^\/d\/([0-9a-f-]{36})\/?$/i)
  if (doc) return { name: 'doc', id: doc[1] }
  if (path === '/' || path === '') return { name: 'list' }
  if (path === '/login' || path === '/login/') return { name: 'login' }
  if (path === '/settings/mcp' || path === '/settings/mcp/') return { name: 'mcp' }
  return { name: 'unknown', path }
}

class Router {
  route = $state<Route>(parse(location.pathname))
  /** Read once at boot, before anything rewrites the address bar. */
  query = $state<URLSearchParams>(new URLSearchParams(location.search))

  constructor() {
    addEventListener('popstate', () => {
      this.route = parse(location.pathname)
      this.query = new URLSearchParams(location.search)
    })
  }

  /** Same-origin navigation without a page load. */
  go(path: string) {
    if (path === location.pathname + location.search) return
    history.pushState({}, '', path)
    this.route = parse(location.pathname)
    this.query = new URLSearchParams(location.search)
    scrollTo(0, 0)
  }

  /**
   * Set a query parameter without adding a history entry.
   *
   * So an outcome that arrived WITHOUT a navigation -- the Google popup
   * posting its result back to the page that opened it -- is reported by the
   * same `?auth_error=` notice as the whole-page redirect, instead of growing
   * a second way to say the same thing.
   */
  setQuery(key: string, value: string) {
    const params = new URLSearchParams(location.search)
    params.set(key, value)
    history.replaceState({}, '', `${location.pathname}?${params.toString()}`)
    this.query = new URLSearchParams(location.search)
  }

  /** Drop a query parameter without adding a history entry. */
  dropQuery(key: string) {
    const params = new URLSearchParams(location.search)
    if (!params.has(key)) return
    params.delete(key)
    const rest = params.toString()
    history.replaceState({}, '', location.pathname + (rest ? `?${rest}` : ''))
    this.query = new URLSearchParams(location.search)
  }
}

export const router = new Router()

/**
 * Intercept a click on an internal link.
 *
 * Modified clicks, new-tab clicks and anything with a target keep the browser's
 * own behaviour; a document link written by an agent must still be openable in
 * a new tab.
 */
export function navigate(event: MouseEvent, path: string) {
  if (event.defaultPrevented) return
  if (event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  router.go(path)
}
